import { AppConstants } from '@/app.constants';
import { API_ENDPOINTS } from '@/api/endpoints.constant';
import { ROUTES } from '@/route/routes.constants';
import { useAppSelector } from '@/store/hooks';
import { AuthState, login } from '@/store/slices/authSlice';
import store from '@/store/store';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import styles from './ClivLogin.module.scss';
import clivLogo from '@/assets/icons/cliv-logo.png';
import darkLogo from '@/assets/icons/logo-dark.png'
import { ClivLoginRequest } from '@/types/ClivLoginRequest.type';
import { clivApiRequest } from '@/utils/apiClient';
import { ApiRequestOptions } from "@/types/ApiRequestOptions";
import { ClivLoginResponse } from '@/types/ClivLoginResponse.type';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
// import { toast } from 'react-toastify';
import { useTranslation } from "react-i18next";
import { LoginErrorCard } from '@/components/LoginError/LoginError';
import { ApprovalPendingCard } from '@/components/ApprovalPending/ApprovalPending';
import Backdrop from '@mui/material/Backdrop';
import { systemConfig } from '@/config/systemConfig';
import { ErrorCode } from '@/enums/ErrorCode.enum';
import { ForbiddenPopup } from '@/components/ForbiddenPopup/ForbiddenPopup';

const AUTH_LOG = '[inspect-auth]';

export const ClivLogin: FC = () => {
  const { t: translate } = useTranslation();
  const loginInfo = useRef<any>(null);
  const idToken = useRef<string>(null);
  const [isClivLoginInProgress, setIsClivLoginInProgress] = useState<boolean>(false);
  const [popupContent, setPopupContent] = useState<React.ReactNode | null>(null);
  const navigate = useNavigate()
  const [loginUrl, setLoginUrl] = useState<string>('');

  const getConfig = useCallback(async () => {
    // eslint-disable-next-line no-console -- login diagnostics
    console.debug(AUTH_LOG, 'ClivLogin: loading B2C config', { tenantId: systemConfig.tenantId, domain: systemConfig.domain });
    const result = await fetch(`https://clivconfig.${systemConfig.domain}/config/${systemConfig.tenantId}.json`);
    const data = await result.json();

    const clientName = data.sk;
    const redirectUrl = systemConfig.isLocal ? 'http://localhost:4201' : `https://${systemConfig.subDomain}.${clientName}.${systemConfig.domain}`
    setLoginUrl(data.login.url + '&redirect_uri=' + encodeURIComponent(redirectUrl));
    loginInfo.current = { apiUrl: systemConfig.apiUrl, clientName };
    // eslint-disable-next-line no-console -- login diagnostics
    console.debug(AUTH_LOG, 'ClivLogin: B2C config loaded, login button href ready');
  }, []);
  const isAuthenticated = useAppSelector(state => state.auth.token !== null);

  useEffect(() => {
    const b2cLogin = async () => {
      if (loginInfo.current && idToken.current && idToken.current.length > 0) {
        // eslint-disable-next-line no-console -- login diagnostics
        console.debug(AUTH_LOG, 'ClivLogin: B2C redirect returned id_token — exchanging at POST /auth/login', {
          tenantId: systemConfig.tenantId,
          appName: AppConstants.APP_NAME,
          idTokenLength: idToken.current.length,
        });
        setIsClivLoginInProgress(true);
        const loginRequest: ClivLoginRequest = {
          token: idToken.current,
          tenantId: systemConfig.tenantId,
          appName: AppConstants.APP_NAME,
        }

        const url = `${systemConfig.apiUrl}/${API_ENDPOINTS.AUTH.LOGIN}`;
        const apiRequest: ApiRequestOptions = {
          url: url,
          body: loginRequest,
          method: 'POST'
        };

        const response = await clivApiRequest<ClivLoginResponse>(apiRequest, true);
        // eslint-disable-next-line no-console -- login diagnostics
        console.debug(AUTH_LOG, 'ClivLogin: B2C token exchange response', {
          ok: response.ok,
          status: response.status,
          requestId: response.requestId,
        });
        if (response.ok && response.data) {
          const loginData = response.data;
          const authUser: AuthState = {
            token: loginData.accessToken,
            user: loginData.user
          }
          // eslint-disable-next-line no-console -- login diagnostics
          console.debug(AUTH_LOG, 'ClivLogin: dispatch login()', { userId: authUser.user?.id });

          store.dispatch(login(authUser));

          // Wait for the state to be updated
          await new Promise<void>((resolve) => {
            const unsubscribe = store.subscribe(() => {
              const state = store.getState();
              if (state.auth.token === loginData.accessToken) {
                // eslint-disable-next-line no-console -- login diagnostics
                console.debug(AUTH_LOG, 'ClivLogin: store updated after login');
                unsubscribe();
                resolve();
              }
            });
          });
          // eslint-disable-next-line no-console -- login diagnostics
          console.debug(AUTH_LOG, 'ClivLogin: navigating', ROUTES.INSPECTIONS);
          navigate(ROUTES.INSPECTIONS);
        } else {
          // eslint-disable-next-line no-console -- login diagnostics
          console.debug(AUTH_LOG, 'ClivLogin: login failed', { status: response?.status, description: response?.data?.description });
          // usage:
          const description = response?.data?.description;
          const status = response?.status;
          setPopupContent(getPopupFor(status, description));

          setIsClivLoginInProgress(false);
        }
      }
    };
    b2cLogin();
  }, [loginInfo.current, idToken.current]);

  const getPopupFor = (status?: number, code?: string) => {
    if (status === 403) {
      if (code === ErrorCode.E403_001) return <ApprovalPendingCard onClose={() => setPopupContent(null)} />;
      if (code === ErrorCode.E403_002) return <ForbiddenPopup onClose={() => setPopupContent(null)} />;
    }
    return <LoginErrorCard onClose={() => setPopupContent(null)} />;
  };

  useEffect(() => {
    if (isAuthenticated) {
      // eslint-disable-next-line no-console -- login diagnostics
      console.debug(AUTH_LOG, 'ClivLogin: authenticated — navigate inspections');
      navigate(ROUTES.INSPECTIONS);
      return;
    }
    const urlParams = new URLSearchParams(window.location.hash);
    idToken.current = urlParams.get('#id_token');
    // eslint-disable-next-line no-console -- login diagnostics
    console.debug(AUTH_LOG, 'ClivLogin: unauthenticated', { hasIdTokenInHash: Boolean(idToken.current?.length) });
    void getConfig();
  }, [getConfig, isAuthenticated, navigate])

  return (
    <>
      <Backdrop open={!!popupContent} sx={{ zIndex: 1300, backgroundColor: "rgba(0, 0, 0, 0.5)", }}>
        {popupContent}
      </Backdrop>

      {/* {popupContent && <Modal>{popupContent}</Modal>} */}
      {isClivLoginInProgress ? (<div className={styles.landingPageContainer}>
        <div className={styles.brandAssetContainer}>
          <img
            src={clivLogo}
            className={styles.logo}
            alt="Logo"
          />
          <div className={styles.loginButtonContainer}>
            <div className={styles.loginPageTextContainer}>
              <span className={`${styles.titleHeader} ${styles.header}`}>{translate('control-live')}</span>
              <span
                className={`${styles.titleHeader} ${styles.subHeader} ${styles.desktopText}`}
              >
                {translate('wherever you are in the world')}
              </span>
              <span className={`${styles.titleHeader} ${styles.subHeader} ${styles.mobileText}`}>
                wherever <br />
                you are in <br />
                the <br />
                world
              </span>
            </div>
            <Box sx={{ display: 'flex' }}>
              <CircularProgress sx={{ color: '#00378C' }} size="4rem" thickness={4} />
            </Box>
            <div className={styles.addressContainer}>
              <span><a href='https://28one.com/'><img
                src={darkLogo}
                className={styles.darkLogo}
                alt="Dark Logo"
              /></a></span>
              <span><a href='https://cliv.io/'><img
                src={clivLogo}
                className={styles.clivLogo}
                alt="Logo"
              /></a></span>
            </div>
            {/* <div className={styles.border}></div> */}
            <div className={styles.bottomContainer}>
              <span className={styles.staticText}>© CLIV {new Date().getFullYear()}.| All Rights Reserved |</span>
              <span className={styles.linkText}><a href='https://cliv.io/terms/'>Terms & Conditions</a></span>
              <span className={styles.staticText}>|</span>
              <span className={styles.linkText}><a href='https://cliv.io/privacy/'>Privacy Policy</a></span>
            </div>
          </div>
        </div>
      </div>) : (
        <div className={styles.landingPageContainer}>
          <div className={styles.brandAssetContainer}>
            <img
              src={clivLogo}
              className={styles.logo}
              alt="Logo"
            />
            <div className={styles.loginButtonContainer}>
              <div className={styles.loginPageTextContainer}>
                <span className={`${styles.titleHeader} ${styles.header}`}>{translate('control-live')}</span>
                <span
                  className={`${styles.titleHeader} ${styles.subHeader} ${styles.desktopText}`}
                >
                  {translate('wherever you are in the world')}
                </span>
                <span className={`${styles.titleHeader} ${styles.subHeader} ${styles.mobileText}`}>
                  wherever <br />
                  you are in <br />
                  the <br />
                  world
                </span>
              </div>
              <a
                className={styles.loginBtn}
                href={loginUrl}
              >
                {translate('Login')}
              </a>
              <div className={styles.addressContainer}>
                <span><a href='https://28one.com/'><img
                  src={darkLogo}
                  className={styles.darkLogo}
                  alt="Dark Logo"
                /></a></span>
                <span><a href='https://cliv.io/'><img
                  src={clivLogo}
                  className={styles.clivLogo}
                  alt="Logo"
                /></a></span>
              </div>
              {/* <div className={styles.border}></div> */}
              <div className={styles.bottomContainer}>
                <span className={styles.staticText}>© CLIV {new Date().getFullYear()}.| All Rights Reserved |</span>
                <span className={styles.linkText}><a href='https://cliv.io/terms/'>Terms & Conditions</a></span>
                <span className={styles.staticText}>|</span>
                <span className={styles.linkText}><a href='https://cliv.io/privacy/'>Privacy Policy</a></span>
              </div>
              <div className={styles.version}>{systemConfig.version}</div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}